import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useRole } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Play, CheckCircle2, Zap, Clock, History, XCircle, User, Bot, Lock } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const CATEGORY_COLOR = {
  Network: "#3B82F6",
  Security: "#EF4444",
  Maintenance: "#A855F7",
  Availability: "#F59E0B",
  IAM: "#10B981",
};

function timeAgo(iso) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Reusable Playbooks panel: catalog + execution console + automation history.
 * Designed to be embedded inside the Agentic Administration page as a tab.
 */
export default function PlaybooksPanel() {
  const { tenant } = useTenant();
  const { canWrite } = useRole();
  const { locale } = useI18n();
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  const loadAll = useCallback(() => {
    if (!tenant) return;
    api.get(`/playbooks?tenant=${tenant.id}`).then(({ data }) => setItems(data));
    api.get(`/automation/history?tenant=${tenant.id}`).then(({ data }) => setHistory(data));
  }, [tenant]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const run = async (pb) => {
    if (!pb.enabled) return toast.error(`${pb.name} is disabled`);
    setRunning(pb.id);
    setLastRun(null);
    try {
      const { data } = await api.post(`/playbooks/${pb.id}/run?tenant=${tenant.id}`);
      const animated = { ...data, log: [] };
      setLastRun(animated);
      for (let i = 0; i < data.log.length; i++) {
        await new Promise((r) => setTimeout(r, 320));
        setLastRun((prev) => ({ ...prev, log: data.log.slice(0, i + 1) }));
      }
      toast.success(`${pb.name} completed`, { description: `${data.log.length} steps OK` });
      api.get(`/automation/history?tenant=${tenant.id}`).then(({ data }) => setHistory(data));
    } catch {
      toast.error("Playbook execution failed");
    } finally {
      setRunning(null);
    }
  };

  const toggle = async (pb, enabled) => {
    setItems((items) => items.map((p) => (p.id === pb.id ? { ...p, enabled } : p)));
    try {
      await api.post(`/playbooks/${pb.id}/toggle`, { tenant: tenant.id, enabled });
      toast.success(`${pb.name} ${enabled ? "enabled" : "disabled"}`);
    } catch {
      setItems((items) => items.map((p) => (p.id === pb.id ? { ...p, enabled: !enabled } : p)));
      toast.error("Toggle failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="playbooks-panel">
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* LEFT: Playbook catalog */}
        <div className="xl:col-span-7 space-y-3">
          {items.map((pb) => {
            const color = CATEGORY_COLOR[pb.category] || "#3B82F6";
            return (
              <div
                key={pb.id}
                className={`border bg-[#0F172A] p-5 transition-all ${pb.enabled ? "border-slate-800 hover:border-slate-700" : "border-slate-900 opacity-60"}`}
                data-testid={`playbook-${pb.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 border" style={{ borderColor: color + "55", background: color + "12" }}>
                    <Zap className="w-5 h-5" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-display font-bold text-lg">{pb.name}</h3>
                      <span className="text-[10px] font-mono font-bold px-2 py-0.5 border uppercase" style={{ color, borderColor: color + "55" }}>
                        {pb.category}
                      </span>
                      {!pb.enabled && (
                        <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-slate-700 text-slate-500 uppercase">
                          {locale === "pt" ? "DESATIVADO" : "DISABLED"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mb-3">
                      {locale === "pt" ? "Gatilho:" : "Trigger:"} <span className="text-slate-200 font-mono">{pb.trigger}</span>
                    </div>
                    <div className="flex items-center gap-4 text-[10px] font-mono text-slate-500 uppercase tracking-wider">
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" />{pb.success_rate}% {locale === "pt" ? "sucesso" : "success"}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{locale === "pt" ? "última" : "last"} {pb.last_run}</span>
                      <span>{pb.steps} {locale === "pt" ? "passos" : "steps"}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase text-slate-500">{pb.enabled ? "On" : "Off"}</span>
                      <Switch
                        data-testid={`toggle-${pb.id}`}
                        checked={pb.enabled}
                        disabled={!canWrite}
                        onCheckedChange={(v) => toggle(pb, v)}
                      />
                    </div>
                    <button
                      data-testid={`run-${pb.id}`}
                      onClick={() => run(pb)}
                      disabled={running === pb.id || !pb.enabled || !canWrite}
                      title={!canWrite ? (locale === "pt" ? "Acesso somente leitura" : "Read-only access") : ""}
                      className="bg-[#1E3A8A] hover:bg-[#1D4ED8] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2 transition-colors"
                    >
                      {canWrite ? <Play className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                      {running === pb.id ? (locale === "pt" ? "Executando..." : "Running...") : (locale === "pt" ? "Executar" : "Execute")}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* RIGHT: Execution console */}
        <div className="xl:col-span-5">
          <div className="border border-slate-800 bg-[#0F172A] sticky top-20">
            <div className="border-b border-slate-800 px-5 py-3 text-xs uppercase tracking-[0.25em] font-mono font-semibold flex items-center justify-between">
              <span>{locale === "pt" ? "Console de Execução" : "Execution Console"}</span>
              {lastRun && <span className="text-[10px] font-mono text-emerald-400 evos-blink">● LIVE</span>}
            </div>
            {!lastRun ? (
              <div className="p-8 text-center text-xs text-slate-500 font-mono">
                <div className="border border-dashed border-slate-800 p-8">
                  {locale === "pt" ? "Execute um playbook para ver a saída ao vivo." : "Execute a playbook to view live console output."}
                </div>
              </div>
            ) : (
              <div className="p-4 font-mono text-[11px] space-y-2 max-h-[380px] overflow-y-auto" data-testid="execution-log">
                <div className="text-slate-500 mb-3">
                  <span className="text-emerald-400">[run]</span> {lastRun.run_id} · {lastRun.playbook}
                </div>
                {lastRun.log.map((step, i) => (
                  <div key={i} className="flex items-start gap-3 border-l-2 border-emerald-500/40 pl-3 py-1">
                    <span className="text-emerald-400">[OK]</span>
                    <span className="text-slate-300 flex-1">{step.step}</span>
                    <span className="text-slate-500">{step.ms}ms</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Automation History */}
      <div className="border border-slate-800 bg-[#0F172A]">
        <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-[#60A5FA]" />
            <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">
              {locale === "pt" ? "Histórico de Automações" : "Automation History"}
            </h3>
          </div>
          <span className="text-[10px] font-mono text-slate-500">{history.length} {locale === "pt" ? "execuções" : "executions"}</span>
        </div>
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto" data-testid="automation-history">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0F172A]">
              <tr className="text-[10px] uppercase font-mono text-slate-500 border-b border-slate-800">
                <th className="text-left px-5 py-3">Timestamp</th>
                <th className="text-left px-5 py-3">Playbook</th>
                <th className="text-left px-5 py-3">{locale === "pt" ? "Disparado por" : "Triggered By"}</th>
                <th className="text-center px-5 py-3">Status</th>
                <th className="text-left px-5 py-3 pr-6">{locale === "pt" ? "Detalhes" : "Details"}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row) => {
                const ok = row.status === "success";
                const isSystem = row.triggered_by_type === "System";
                return (
                  <tr key={row.run_id} className="border-b border-slate-800/40 hover:bg-[#1E293B]/40" data-testid={`history-row-${row.run_id}`}>
                    <td className="px-5 py-2.5 font-mono text-[11px] text-slate-300">
                      {new Date(row.started_at).toLocaleString(locale === "pt" ? "pt-BR" : "en-GB", { hour12: false })}
                      <div className="text-[9px] text-slate-500">{timeAgo(row.started_at)}</div>
                    </td>
                    <td className="px-5 py-2.5">
                      <div className="text-xs text-slate-100 font-semibold">{row.playbook_name}</div>
                      <div className="text-[9px] font-mono text-slate-500">{row.run_id}</div>
                    </td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-1.5">
                        {isSystem ? <Bot className="w-3 h-3 text-[#60A5FA]" /> : <User className="w-3 h-3 text-emerald-400" />}
                        <div>
                          <div className="text-xs text-slate-200">{row.triggered_by_type}</div>
                          <div className="text-[9px] font-mono text-slate-500 truncate max-w-[160px]">{row.triggered_by}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-center">
                      <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border whitespace-nowrap ${ok ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-red-300 border-red-500/40 bg-red-500/10"}`}>
                        {ok ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <XCircle className="w-3 h-3 inline mr-1" />}
                        {row.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 pr-6 text-[11px] text-slate-400">{row.details}</td>
                  </tr>
                );
              })}
              {!history.length && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-xs text-slate-500 font-mono">
                  {locale === "pt" ? "Nenhuma execução ainda" : "No executions yet"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
